// THE PAYMENTS ON ONE CHARGE, and undoing one recorded by mistake.
//
// The money screen shows a charge's collected TOTAL; this lists the installments behind
// it (GET /invoices/:id/payments) with an undo on each. Undo is two steps in one dialog —
// the list, then a confirmation — and the confirmation is the point: it states, per
// partner BY NAME, how the gap between them will move, as the server computed it from the
// unit's own split (calculations/paymentRemoval; check:unpay holds it to the real gap).
//
// The deletion is outright and commits with the invoice's status recompute on the server
// (payments/remove.js). There is no edit: a wrong amount or date is undone and recorded
// again, so the month a payment lands in only ever changes by a deliberate act.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import { useLookups } from '../../context/LookupsContext';
import { paymentsApi } from '../../api';
import he from '../../i18n/he';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import DataTable from '../../components/DataTable';
import Money, { formatMoney } from '../../components/Money';
import DateText from '../../components/DateText';
import AsyncBoundary, { ErrorText } from '../../components/Loader';
import ChargeHeader from '../../components/ChargeHeader';

const t = he.collections.board;

// One partner's movement, in the settlement screen's words. Zero is not listed: a
// partner who holds no share and did not issue the charge is simply not moved.
const GapLine = ({ g }) => (
  <li>{g.delta > 0
    ? t.undoGapCredit(g.name, formatMoney(g.delta))
    : t.undoGapDebit(g.name, formatMoney(-g.delta))}</li>
);

const Confirm = ({ preview }) => (
  <>
    <ul className="consequences">
      <li>{t.undoLead(formatMoney(preview.amount))}</li>
      <li>
        {t.undoGapLead}
        <ul>
          {preview.gap.filter((g) => Math.abs(g.delta) > 0.005)
            .map((g) => <GapLine key={g.partnerId} g={g} />)}
        </ul>
      </li>
    </ul>
  </>
);

export default function ChargePaymentsModal({ card, row, onClose, onSaved }) {
  const lookups = useLookups();
  const list = useAsync(() => paymentsApi.byInvoice(row.invoiceId));
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const ask = async (payment) => {
    setError(null);
    try {
      setPreview(await paymentsApi.removalPreview(payment.id));
    } catch (e) {
      setError(e);
    }
  };
  const undo = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await paymentsApi.remove(preview.paymentId);
      onSaved();
    } catch (e) {
      setError(e);
      setSaving(false);
    }
  };

  if (preview) {
    return (
      <Modal title={t.undoTitle} onClose={onClose} onSave={undo} saveLabel={t.undoConfirm} saveDisabled={saving}>
        <ChargeHeader card={card} row={row} />
        <Confirm preview={preview} />
        <Button small variant="subtle" onClick={() => { setPreview(null); setError(null); }}>{t.undoBack}</Button>
        {error && <ErrorText error={error} />}
      </Modal>
    );
  }

  const columns = [
    { key: 'payment_date', label: t.date, primary: true, render: (p) => <DateText value={p.payment_date} /> },
    { key: 'amount', label: t.amount, className: 'num', render: (p) => <Money value={p.amount} /> },
    { key: 'method', label: he.payment.method, render: (p) => lookups.methodLabel(p.method) },
    // Each instalment names who banked it (068) — the partner the gap credits it to.
    { key: 'collected_by_name', label: he.payment.collectedBy, render: (p) => p.collected_by_name || '—' },
    { key: 'action', label: he.common.actions, action: true,
      render: (p) => <Button small variant="subtle" onClick={() => ask(p)}>{t.undoPayment}</Button> },
  ];

  return (
    <Modal title={t.paymentsTitle} onClose={onClose} onSave={onClose} saveLabel={he.common.close}>
      <ChargeHeader card={card} row={row} />
      <AsyncBoundary loading={list.loading} error={list.error}>
        <DataTable columns={columns} rows={list.data || []} empty={{ text: t.paymentsNone }} />
      </AsyncBoundary>
      {error && <ErrorText error={error} />}
    </Modal>
  );
}
